import React, { useEffect, useRef } from "react";

// EK-442 — Indicador de áudio (VU meter) desenhado em Canvas, sem libs externas.
// - mode="listening": usa FFT real do microfone (MediaStream) para refletir a voz do usuário.
// - mode="speaking": animação sintética de onda enquanto o Líder fala.
// - mode="idle": barras achatadas.

export interface VUMeterProps {
	mode: "listening" | "speaking" | "idle";
	/** Stream do microfone para análise real (apenas em listening). */
	stream?: MediaStream | null;
	color?: string;
	bars?: number;
	width?: number;
	height?: number;
}

export const VUMeter: React.FC<VUMeterProps> = ({ mode, stream, color = "#1DB954", bars = 24, width = 220, height = 64 }) => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const rafRef = useRef<number | null>(null);
	const audioCtxRef = useRef<AudioContext | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
	const freqDataRef = useRef<Uint8Array | null>(null);
	const phaseRef = useRef(0);

	// Configura/desmonta o grafo de áudio quando há stream em modo listening.
	useEffect(() => {
		const teardown = () => {
			try {
				sourceRef.current?.disconnect();
			} catch {
				/* noop */
			}
			sourceRef.current = null;
			if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
				audioCtxRef.current.close().catch(() => undefined);
			}
			audioCtxRef.current = null;
			analyserRef.current = null;
			freqDataRef.current = null;
		};

		if (mode === "listening" && stream) {
			try {
				const AudioCtx = window.AudioContext ?? (window as any).webkitAudioContext;
				const ctx: AudioContext = new AudioCtx();
				const analyser = ctx.createAnalyser();
				analyser.fftSize = 64;
				analyser.smoothingTimeConstant = 0.7;
				const source = ctx.createMediaStreamSource(stream);
				source.connect(analyser);
				audioCtxRef.current = ctx;
				analyserRef.current = analyser;
				sourceRef.current = source;
				freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
			} catch {
				teardown();
			}
		} else {
			teardown();
		}

		return teardown;
	}, [mode, stream]);

	// Loop de desenho.
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const dpr = window.devicePixelRatio || 1;
		canvas.width = width * dpr;
		canvas.height = height * dpr;
		ctx.scale(dpr, dpr);

		const barWidth = width / bars;
		const gap = barWidth * 0.35;

		const draw = () => {
			ctx.clearRect(0, 0, width, height);
			const mid = height / 2;

			const amplitudes: number[] = new Array(bars).fill(0);

			if (mode === "listening" && analyserRef.current && freqDataRef.current) {
				analyserRef.current.getByteFrequencyData(freqDataRef.current as any);
				const data = freqDataRef.current;
				const step = Math.max(1, Math.floor(data.length / bars));
				for (let i = 0; i < bars; i++) {
					const v = data[i * step] / 255; // 0..1
					amplitudes[i] = v;
				}
			} else if (mode === "speaking") {
				phaseRef.current += 0.15;
				for (let i = 0; i < bars; i++) {
					const wave = Math.sin(phaseRef.current + i * 0.5) * 0.5 + 0.5;
					const envelope = Math.sin((i / bars) * Math.PI); // mais alto no centro
					amplitudes[i] = wave * envelope * 0.85 + 0.08;
				}
			} else {
				// idle: leve respiração
				phaseRef.current += 0.04;
				for (let i = 0; i < bars; i++) {
					amplitudes[i] = 0.06 + Math.sin(phaseRef.current + i * 0.3) * 0.03 + 0.03;
				}
			}

			for (let i = 0; i < bars; i++) {
				const barHeight = Math.max(2, amplitudes[i] * (height - 6));
				const x = i * barWidth + gap / 2;
				const y = mid - barHeight / 2;
				ctx.fillStyle = color;
				const radius = Math.min((barWidth - gap) / 2, barHeight / 2);
				// Barra com cantos arredondados
				ctx.beginPath();
				const w = barWidth - gap;
				if (typeof (ctx as any).roundRect === "function") {
					(ctx as any).roundRect(x, y, w, barHeight, radius);
				} else {
					ctx.rect(x, y, w, barHeight);
				}
				ctx.fill();
			}

			rafRef.current = requestAnimationFrame(draw);
		};

		draw();

		return () => {
			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		};
	}, [mode, color, bars, width, height]);

	return (
		<canvas
			ref={canvasRef}
			style={{ width, height }}
			aria-hidden
		/>
	);
};
